import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'warning'
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const getVariantClasses = () => {
    switch (variant) {
      case 'danger':
        return {
          icon: 'text-red-600',
          confirmButton: 'btn-danger',
          iconBg: 'bg-red-100'
        };
      case 'warning':
        return {
          icon: 'text-gray-600',
          confirmButton: 'bg-gray-600 text-white hover:bg-gray-700 btn',
          iconBg: 'bg-gray-100'
        };
      case 'info':
        return {
          icon: 'text-gray-600',
          confirmButton: 'btn-primary',
          iconBg: 'bg-gray-100'
        };
      default:
        return {
          icon: 'text-gray-600',
          confirmButton: 'bg-gray-600 text-white hover:bg-gray-700 btn',
          iconBg: 'bg-gray-100'
        };
    }
  };

  const classes = getVariantClasses();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-2xl transform transition-all">
        <div className="p-6">
          <div className="flex items-start">
            <div className={`flex-shrink-0 rounded-full p-3 ${classes.iconBg} mr-4`}>
              <AlertTriangle className={`h-6 w-6 ${classes.icon}`} />
            </div>
            
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {message}
              </p>
            </div>
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex space-x-3 justify-end">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            className={classes.confirmButton}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal; 